import { computed, ref, onMounted, watch, Ref, ComputedRef } from 'vue';
import { differenceInHours, isPast } from 'date-fns';
import { User } from '@/types/user';

const pluralize = (value: string, count: number) => (count !== 1 ? value + 's' : value);

const INELIGIBLE_ROLES = ['partner-admin', 'partner-owner', 'group-admin', 'group-owner'];

interface CountdownResult {
	days: number;
	hours: number;
	formattedCountdown: string;
	formattedDate: {
		days: string;
		hours: string;
		daysLabel: string;
		hoursLabel: string;
	};
	isExpired: boolean;
}

type UserArg = User | Ref<User> | ComputedRef<User> | null | undefined;

export function useAcademicStudentAccessExpiration(user: UserArg) {
	const countdown = ref<CountdownResult>({
		days: 0,
		hours: 0,
		formattedCountdown: '',
		formattedDate: {
			days: '0',
			hours: '0',
			daysLabel: 'days',
			hoursLabel: 'hours',
		},
		isExpired: false,
	});

	const getUser = () => user && typeof user === 'object' && 'value' in user
		? (user as Ref<User>).value
		: user as User | null | undefined;

	const calculateCountdown = () => {
		const u = getUser();
		const dateValue = u?.access_expiration_date;

		if (!dateValue) {
			countdown.value = {
				days: 0,
				hours: 0,
				formattedCountdown: '',
				formattedDate: {
					days: '0',
					hours: '0',
					daysLabel: 'days',
					hoursLabel: 'hours',
				},
				isExpired: true,
			};
			return;
		}

		const expDate = new Date(dateValue);
		const now = new Date();

		if (isPast(expDate)) {
			countdown.value = {
				days: 0,
				hours: 0,
				formattedCountdown: 'Access expired',
				formattedDate: {
					days: '0',
					hours: '0',
					daysLabel: 'days',
					hoursLabel: 'hours',
				},
				isExpired: true,
			};
			return;
		}

		const totalHoursRemaining = differenceInHours(expDate, now);
		const days = Math.floor(totalHoursRemaining / 24);
		const hours = totalHoursRemaining % 24;

		countdown.value = {
			days,
			hours,
			formattedCountdown: `${days} ${pluralize('day', days)} | ${hours} ${pluralize('hour', hours)}`,
			formattedDate: {
				days: String(days),
				hours: String(hours),
				daysLabel: pluralize('day', days),
				hoursLabel: pluralize('hour', hours),
			},
			isExpired: false,
		};
	};

	onMounted(() => {
		calculateCountdown();
	});

	// Watch for changes if a ref/computed is passed
	if (typeof user === 'object' && user && 'value' in user) {
		watch(user, calculateCountdown);
	}

	return {
		countdown: computed(() => countdown.value),
		isEligible: computed(() => {
			const u = getUser();
			return !!u && u.type === 'academic' && !INELIGIBLE_ROLES.includes(u.role);
		}),
	};
}
